import { yupResolver } from '@hookform/resolvers/yup'
import { map, findIndex } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { ChevronDown, ChevronUp, Plus } from 'lucide-react'

import Loader from '../../../common/components/Loader'
import { IBenefits, ITranslation, Languages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  createShareReferralBenefits,
  createShareReferralDescription,
  deleteShareReferralBenefit,
  getShareReferralBenefits,
  getShareReferralDescription,
  updateShareReferralBenefits,
} from '../../../core/services/referrals'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import SelectField from '@/components/select-field'

export interface Benefits {
  benefits: {
    id?: string
    amount?: number
    amountCurrency: boolean
    currencyType?: 'COIN' | 'MAIN_CURRENCY'
    ordinal: number
    translations: ITranslation[]
  }[]
}

export interface Translations {
  translations: ITranslation[]
}

export const ReferralBenefits: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'REFERRALS' })
  const [benefits, setBenefits] = useState<IBenefits[]>([])
  const [translations, setTranslations] = useState<ITranslation[]>([])
  const [showDeleteConfirmationModalBen, setDeleteConfirmationModalBen] =
    useState<boolean>(false)
  const [removeBenefitState, setRemoveBenefitState] = useState<number>()
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const defaultTranslations = map(allLanguages, (lang) => {
    return {
      locale: lang.locale,
      description: '',
    }
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        setAllLanguages(allLanguagesResponse.data)
        const benef = await getShareReferralBenefits()
        if (benef.status === 200) {
          setBenefits(benef.data)
          const temp = map(benef.data, (benefit) => {
            const transTemp = map(allLanguagesResponse.data, lang => {
              return {
                locale: lang.locale,
                description: '',
              }
            })
            map(benefit.translations, trans => {
              const index = findIndex(allLanguagesResponse.data, l => l.locale === trans.locale)
              if (index !== -1) {
                transTemp[index].locale = trans.locale
                transTemp[index].description = trans.description
              }
            })
            return ({
              id: benefit.id,
              amount: benefit.amount === null ? undefined : benefit.amount,
              currencyType: benefit.currencyType === null ? undefined : benefit.currencyType,
              amountCurrency: benefit.amount !== null || benefit.currencyType !== null ? true : false,
              ordinal: benefit.ordinal,
              translations: transTemp,
            })
          })
          setTimeout(() => {
            setBenefitValues('benefits', temp)
          }, 500)
        }

        const trans = await getShareReferralDescription()
        if (trans.status === 200) {
          setTranslations(trans.data)
          const transTemp = map(allLanguagesResponse.data, lang => {
            return {
              locale: lang.locale,
              description: '',
            }
          })
          if (!!trans.data && trans.data.length > 0) {
            map(trans.data, t => {
              const index = findIndex(allLanguagesResponse.data, l => l.locale === t.locale)
              if (index !== -1) {
                transTemp[index] = {
                  locale: t.locale,
                  description: t.description,
                }
              }
            })
            setTimeout(() => {
              setTranslationValues('translations', transTemp)
            }, 300)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const deleteBenefit = async () => {
    if (!hasEditPermission) {
      return
    }
    if (removeBenefitState === undefined) {
      return
    }
    if (benefits[removeBenefitState]?.id === undefined) {
      removeBenefit(removeBenefitState)
      return
    }
    try {
      setLoading(true)
      const deleteResponse = await deleteShareReferralBenefit(
        benefits[removeBenefitState]?.id as string,
      )
      removeBenefit(removeBenefitState)
      const getResponse = await getShareReferralBenefits()
      if (getResponse.status === 200 && deleteResponse.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
        setBenefits(getResponse.data)
        const temp = map(getResponse.data, (benefit) => {
          return {
            id: benefit.id,
            amount: benefit.amount === null ? undefined : benefit.amount,
            currencyType:
              benefit.currencyType === null ? undefined : benefit.currencyType,
            amountCurrency:
              benefit.amount !== null || benefit.currencyType !== null
                ? true
                : false,
            ordinal: benefit.ordinal,
            translations: benefit.translations,
          }
        })
        setBenefitValues('benefits', temp)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const schemaTranslations = yup.object().shape({
    translations: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          id: yup.string(),
          locale: yup.string().required('Field required!'),
          description: yup.string().required('Field required!'),
        }),
      )
      .required(),
  })

  const schemaBenefits = yup.object().shape({
    benefits: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          amountCurrency: yup.boolean().required(),
          amount: yup.number().when('amountCurrency', (amountCurrency) => {
            if (amountCurrency) {
              return yup.string().notRequired()
            } else {
              return yup.string().required('Field is required')
            }
          }),
          currencyType: yup
            .string()
            .oneOf(['COIN', 'MAIN_CURRENCY'])
            .when('amountCurrency', (amountCurrency) => {
              if (amountCurrency) {
                return yup.string().notRequired()
              } else {
                return yup.string().required('Field is required')
              }
            }),
          ordinal: yup.number().required('Field required!'),
          translations: yup
            .array()
            .min(1)
            .of(
              yup.object().shape({
                id: yup.string(),
                locale: yup.string().required('Field required!'),
                description: yup.string().required('Field required!'),
              }),
            )
            .required(),
        }),
      )
      .required(),
  })

  const {
    register: registerTranslations,
    formState: { errors: errorsTranslations },
    setValue: setTranslationValues,
    handleSubmit: handleTranslationSubmit,
  } = useForm<Translations>({
    mode: 'onChange',
    resolver: yupResolver<Translations>(schemaTranslations),
  })

  const {
    register: registerBenefits,
    formState: { errors: errorsBenefits },
    setValue: setBenefitValues,
    trigger: triggerBenefits,
    watch: watchBenefits,
    control: controlBenefits,
  } = useForm<Benefits>({
    mode: 'onChange',
    resolver: yupResolver<Benefits>(schemaBenefits),
  })

  const {
    fields: benefitFields,
    append: appendBenefit,
    remove: removeBenefit,
  } = useFieldArray({
    control: controlBenefits,
    name: `benefits`,
  })

  const benefitsWatch = watchBenefits(`benefits`)

  const changeOrder = async (direction: number, index: number) => {
    if (!hasEditPermission) {
      return
    }
    const data = watchBenefits('benefits')

    data[index].ordinal = index + direction
    data[index + direction].ordinal = index
    const firstId = data[index].id
    const secondId = data[index + direction].id

    try {
      setLoading(true)
      if (secondId && firstId) {
        const [responseUpdate1, responseUpdate2] = await Promise.all([
          updateShareReferralBenefits(data[index], secondId),
          updateShareReferralBenefits(data[index + direction], firstId),
        ])
        const allBenefitsResponse = await getShareReferralBenefits()
        if (
          responseUpdate1.status === 200 &&
          responseUpdate2.status === 200 &&
          allBenefitsResponse.status === 200
        ) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
          setBenefits(allBenefitsResponse.data)
          const temp = map(allBenefitsResponse.data, (benefit) => {
            const transTemp = defaultTranslations
            map(benefit.translations, (trans) => {
              const index = findIndex(
                allLanguages,
                (l) => l.locale === trans.locale,
              )
              if (index !== -1) {
                transTemp[index].locale = trans.locale
                transTemp[index].description = trans.description
              }
            })
            return {
              id: benefit.id,
              amount: benefit.amount === null ? undefined : benefit.amount,
              currencyType:
                benefit.currencyType === null
                  ? undefined
                  : benefit.currencyType,
              amountCurrency:
                benefit.amount !== null || benefit.currencyType !== null
                  ? true
                  : false,
              ordinal: benefit.ordinal,
              translations: transTemp,
            }
          })
          setBenefitValues('benefits', temp)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancelBenefits = async (index: number) => {
    if (!hasEditPermission) {
      return
    }
    setTimeout(() => {
      setBenefitValues(`benefits.${index}.id`, benefits[index].id)
      setBenefitValues(`benefits.${index}.amount`, benefits[index].amount)
      setBenefitValues(
        `benefits.${index}.currencyType`,
        benefits[index].currencyType,
      )
      setBenefitValues(
        `benefits.${index}.amountCurrency`,
        benefits[index].amount !== null || benefits[index].currencyType !== null
          ? true
          : false,
      )
      setBenefitValues(`benefits.${index}.ordinal`, benefits[index].ordinal)
      const transTemp = defaultTranslations
      map(benefits[index].translations, (trans) => {
        const index = findIndex(allLanguages, (l) => l.locale === trans.locale)
        if (index !== -1) {
          transTemp[index].locale = trans.locale
          transTemp[index].description = trans.description
        }
      })
      setBenefitValues(`benefits.${index}.translations`, transTemp)
    }, 300)
  }

  const handleCancelTranslation = async () => {
    if (!hasEditPermission) {
      return
    }
    const transTemp = defaultTranslations
    map(translations, (trans) => {
      const index = findIndex(allLanguages, (l) => l.locale === trans.locale)
      if (index !== -1) {
        transTemp[index].locale = trans.locale
        transTemp[index].description = trans.description
      }
    })
    setTranslationValues(`translations`, transTemp)
  }

  const submitBenefits = async (index: number) => {
    if (!hasEditPermission) {
      return
    }
    const isValid = await triggerBenefits(`benefits.${index}`)
    if (!isValid) return
    const benefit = watchBenefits(`benefits.${index}`)
    try {
      setLoading(true)
      let data: IBenefits
      if (benefit.amountCurrency) {
        data = {
          id: benefit.id,
          amount: benefit.amount,
          currencyType: benefit.currencyType,
          ordinal: benefit.ordinal,
          translations: benefit.translations,
        }
      } else {
        data = {
          id: benefit.id,
          ordinal: benefit.ordinal,
          translations: benefit.translations,
        }
      }
      if (!!benefits[index]) {
        const updateId = benefit.id
        if (!!updateId) {
          const updateBenefitsResponse = await updateShareReferralBenefits(
            data,
            updateId,
          )
          const allBenefitsResponse = await getShareReferralBenefits()
          if (
            updateBenefitsResponse.status === 200 &&
            allBenefitsResponse.status === 200
          ) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Update Successful')
            setBenefits(allBenefitsResponse.data)
            const temp = map(allBenefitsResponse.data, (benefit) => {
              return {
                id: benefit.id,
                amount: benefit.amount === null ? undefined : benefit.amount,
                currencyType:
                  benefit.currencyType === null
                    ? undefined
                    : benefit.currencyType,
                amountCurrency:
                  benefit.amount !== null || benefit.currencyType !== null
                    ? true
                    : false,
                ordinal: benefit.ordinal,
                translations: benefit.translations,
              }
            })
            setBenefitValues('benefits', temp)
          }
        }
      } else {
        const createResponse = await createShareReferralBenefits(data)
        const getResponse = await getShareReferralBenefits()
        if (createResponse.status === 200 && getResponse.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
          setBenefits(getResponse.data)
          const temp = map(getResponse.data, (benefit) => {
            return {
              id: benefit.id,
              amount: benefit.amount === null ? undefined : benefit.amount,
              currencyType:
                benefit.currencyType === null
                  ? undefined
                  : benefit.currencyType,
              amountCurrency:
                benefit.amount !== null || benefit.currencyType !== null
                  ? true
                  : false,
              ordinal: benefit.ordinal,
              translations: benefit.translations,
            }
          })
          setBenefitValues('benefits', temp)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const submitTranslations = async (values: Translations) => {
    if (!hasEditPermission) {
      return
    }
    try {
      setLoading(true)
      const response = await createShareReferralDescription(values.translations)
      if (response.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
        setTranslations(response.data)
        setTranslationValues('translations', response.data)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading && <Loader />}
      <div className="flex flex-col gap-3 mb-3">
        <PageTitle title="Share Referral Description" />

        <Card className="border border-monochrome-120">
          <div className="p-3 w-full bg-monochrome-5-50">
            <form
              id="share-referral-translations"
              onSubmit={handleTranslationSubmit(submitTranslations)}
            >
              <div className="flex flex-col w-full gap-2">
                {map(allLanguages, (language, index) => {
                  return (
                    <div
                      className="flex flex-col p-3 gap-2 w-full bg-monochrome-5-50 rounded-[10px] border border-monochrome-120"
                      key={language.id}
                    >
                      <FormGroup
                        label="Language:"
                        direction="row"
                        wrapperClassName="mb-0"
                      >
                        <span className="text-base text-accent-1 font-bold leading-[150%]">
                          {language.name}
                        </span>
                      </FormGroup>
                      <FormGroup label="Translation" wrapperClassName="mb-0">
                        <Input
                          isInvalid={
                            !!errorsTranslations?.translations?.[index]
                              ?.description?.message
                          }
                          {...registerTranslations(
                            `translations.${index}.description`,
                          )}
                          type="text"
                        />
                      </FormGroup>
                    </div>
                  )
                })}
              </div>
            </form>
          </div>
          {hasEditPermission && (
            <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
              <Button
                onClick={() => handleCancelTranslation()}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="share-referral-translations"
                variant="primary"
              >
                Save
              </Button>
            </div>
          )}
        </Card>

        <PageTitle title="Benefits" />
        {map(benefitFields, (benefit, index) => {
          return (
            <Card
              key={benefit.id}
              className="border border-monochrome-120 mb-4"
            >
              <form>
                <div className="p-3">
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex gap-2 justify-between items-center w-full">
                      <div className="flex gap-2 items-center">
                        <Button
                          variant="secondary"
                          className="border border-monochrome-120"
                          disabled={benefit.ordinal === 0}
                          type="button"
                          onClick={() => changeOrder(-1, index)}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <span>{benefit.ordinal + 1}</span>
                        <Button
                          variant="secondary"
                          type="button"
                          className="border border-monochrome-120"
                          disabled={benefit.ordinal >= benefitFields.length - 1}
                          onClick={() => changeOrder(1, index)}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      {hasEditPermission && (
                        <Button
                          variant="secondary"
                          className="bg-accent-3 text-white hover:bg-accent-3/90"
                          type="button"
                          onClick={() => {
                            setDeleteConfirmationModalBen(true)
                            setRemoveBenefitState(index)
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                    <FormGroup
                      label="Amount & Currency"
                      direction="row"
                      labelWrapperClassName="me-4"
                      labelProps={{ style: { width: '150px' } }}
                      wrapperClassName="items-center"
                    >
                      <Controller
                        name={`benefits.${index}.amountCurrency`}
                        control={controlBenefits}
                        render={({ field }) => (
                          <Checkbox
                            checked={Boolean(field.value)}
                            onCheckedChange={(checked) =>
                              field.onChange(Boolean(checked))
                            }
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup label="Ammount" wrapperClassName="w-full">
                      <Input
                        isInvalid={
                          !!errorsBenefits?.benefits?.[index]?.amount?.message
                        }
                        {...registerBenefits(`benefits.${index}.amount`)}
                        type="number"
                        disabled={!benefitsWatch?.[index]?.amountCurrency}
                        min={0}
                        placeholder="Enter Ammount"
                      />
                    </FormGroup>
                    <FormGroup label="Currency" wrapperClassName="w-full">
                      <Controller
                        name={`benefits.${index}.currencyType`}
                        control={controlBenefits}
                        render={({ field }) => (
                          <SelectField
                            {...field}
                            disabled={!benefitsWatch?.[index]?.amountCurrency}
                            error={
                              errorsBenefits?.benefits?.[index]?.currencyType
                                ?.message
                            }
                            placeholder="Select A Currency"
                            options={[
                              { label: 'Custom Coin', value: 'COIN' },
                              {
                                label: 'Main Currency',
                                value: 'MAIN_CURRENCY',
                              },
                            ]}
                          />
                        )}
                      />
                    </FormGroup>
                    <div className="flex flex-col w-full gap-2">
                      {map(allLanguages, (language, i) => {
                        return (
                          <div
                            className="flex flex-col p-3 gap-2 w-full bg-monochrome-5-50 rounded-[10px] border border-monochrome-120"
                            key={language.id}
                          >
                            <FormGroup
                              label="Language:"
                              direction="row"
                              wrapperClassName="mb-0"
                            >
                              <span className="text-base text-accent-1 font-bold leading-[150%]">
                                {language.name}
                              </span>
                            </FormGroup>
                            <FormGroup
                              label="Translation"
                              wrapperClassName="mb-0"
                            >
                              <Input
                                isInvalid={
                                  !!errorsBenefits?.benefits?.[index]
                                    ?.translations?.[i]?.description?.message
                                }
                                {...registerBenefits(
                                  `benefits.${index}.translations.${i}.description`,
                                )}
                                type="text"
                              />
                            </FormGroup>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
                {hasEditPermission && (
                  <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
                    <Button
                      onClick={() => handleCancelBenefits(index)}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        submitBenefits(index)
                      }}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </form>
            </Card>
          )
        })}
      </div>
      {hasEditPermission && (
        <Button
          onClick={() =>
            appendBenefit({
              id: undefined,
              amount: undefined,
              amountCurrency: false,
              currencyType: undefined,
              ordinal: benefitFields.length,
              translations: defaultTranslations,
            })
          }
          type="button"
          variant="secondary"
          className="w-full mt-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Benefit
        </Button>
      )}
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalBen}
        setDeleteConfirmationModal={setDeleteConfirmationModalBen}
        deleteTitle={'Benefit'}
        deleteFunc={deleteBenefit}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
